import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CurrencyRupeeIcon,
  PencilIcon,
  RefreshIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CalculatorIcon,
  AdjustmentsIcon,
} from "@heroicons/react/outline";
import {
  getExchangeChargesThunk,
  updateExchangeChargesThunk,
  getDefaultExchangeChargesThunk,
  resetExchangeChargesThunk,
  validateExchangeChargesThunk,
} from "../../../store/thunks/branchThunks";
import ExchangeChargesCopyModal from "../modals/ExchangeChargesCopyModal";
import NumberInput from "../../common/NumberInput";

const ExchangeChargesManagement = ({ branch }) => {
  const dispatch = useDispatch();
  const { exchangeCharges, loadingExchange } = useSelector(
    (state) => state.branch
  );
  const { user: currentAdmin } = useSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const isRootAdmin = currentAdmin?.role === "root";

  useEffect(() => {
    if (branch?._id) {
      dispatch(getExchangeChargesThunk(branch._id));
    }
  }, [dispatch, branch?._id]);

  useEffect(() => {
    if (exchangeCharges?.current) {
      setFormData(exchangeCharges.current.exchangeCharges || {});
    }
  }, [exchangeCharges]);

  const handleEdit = async () => {
    if (!isRootAdmin) {
      alert("Only root admins can edit exchange charges");
      return;
    }

    // Load defaults for reference
    await dispatch(getDefaultExchangeChargesThunk());
    setIsEditing(true);
  };

  const handleSave = async () => {
    // Validate before saving
    const validationResult = await dispatch(
      validateExchangeChargesThunk(formData)
    ).unwrap();

    if (!validationResult.valid) {
      setValidationErrors(validationResult.errors || []);
      return;
    }

    try {
      await dispatch(
        updateExchangeChargesThunk({
          branchId: branch._id,
          exchangeCharges: formData,
        })
      ).unwrap();

      setIsEditing(false);
      setValidationErrors([]);
      alert("Exchange charges updated successfully");
    } catch (error) {
      alert(`Failed to update exchange charges: ${error}`);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(exchangeCharges?.current?.exchangeCharges || {});
    setValidationErrors([]);
  };

  const handleReset = async () => {
    if (!isRootAdmin) {
      alert("Only root admins can reset exchange charges");
      return;
    }

    if (
      window.confirm(
        "Reset exchange charges to defaults? This will override all custom settings."
      )
    ) {
      try {
        await dispatch(resetExchangeChargesThunk(branch._id)).unwrap();
        alert("Exchange charges reset to defaults");
        setIsEditing(false);
      } catch (error) {
        alert(`Failed to reset exchange charges: ${error}`);
      }
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const formatCurrency = (value) => {
    return value ? `₹${value.toLocaleString("en-IN")}` : "₹0";
  };

  if (loadingExchange) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const charges = formData;

  return (
    <div className="space-y-6">
      {showCopyModal && (
        <ExchangeChargesCopyModal
          isOpen={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          sourceBranch={branch}
        />
      )}
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center">
          <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
          GOLD Exchange Charges Configuration
        </h3>
        <div className="flex gap-2">
          {!isEditing ? (
            <>
              {isRootAdmin && (
                <>
                  <button
                    onClick={() => setShowCopyModal(true)}
                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    Copy to Other Branches
                  </button>
                  <button
                    onClick={handleReset}
                    className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 text-sm"
                  >
                    <RefreshIcon className="h-4 w-4" />
                    Reset to Defaults
                  </button>
                  <button
                    onClick={handleEdit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
            <div>
              <h4 className="text-sm font-medium text-red-900">
                Validation Errors
              </h4>
              <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Deductions */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-4 flex items-center">
            <CalculatorIcon className="h-4 w-4 mr-2" />
            Basic Deductions
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Melting Loss (%)
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.meltingLossPercent || 0}
                  onChange={(value) =>
                    handleInputChange("meltingLossPercent", value)
                  }
                  suffix="%"
                  step="0.1"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {charges.meltingLossPercent || 1.5}%
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Wastage (%)
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.wastagePercent || 0}
                  onChange={(value) =>
                    handleInputChange("wastagePercent", value)
                  }
                  suffix="%"
                  step="0.1"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">{charges.wastagePercent || 2}%</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Stone Deduction (per gram)
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.stoneDeductionPerGram || 0}
                  onChange={(value) =>
                    handleInputChange("stoneDeductionPerGram", value)
                  }
                  prefix="₹"
                  step="10"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {formatCurrency(charges.stoneDeductionPerGram || 50)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Charges */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-4 flex items-center">
            <CurrencyRupeeIcon className="h-4 w-4 mr-2" />
            Fixed Charges
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Handling Charges
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.handlingCharges || 0}
                  onChange={(value) =>
                    handleInputChange("handlingCharges", value)
                  }
                  prefix="₹"
                  step="50"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {formatCurrency(charges.handlingCharges || 100)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Testing Charges
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.testingCharges || 0}
                  onChange={(value) =>
                    handleInputChange("testingCharges", value)
                  }
                  prefix="₹"
                  step="25"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {formatCurrency(charges.testingCharges || 50)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Condition-based Deductions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium mb-4 flex items-center">
            <AdjustmentsIcon className="h-4 w-4 mr-2" />
            Condition Deductions (%)
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {["excellent", "good", "fair", "poor"].map((condition) => (
              <div key={condition}>
                <label className="block text-sm text-gray-600 mb-1 capitalize">
                  {condition}
                </label>
                {isEditing ? (
                  <NumberInput
                    value={charges.conditionDeductions?.[condition] || 0}
                    onChange={(value) =>
                      handleInputChange(
                        `conditionDeductions.${condition}`,
                        value
                      )
                    }
                    suffix="%"
                    step="1"
                    className="w-full"
                    defaultToZero={true}
                  />
                ) : (
                  <p className="font-medium">
                    {charges.conditionDeductions?.[condition] || 0}%
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Transaction Limits */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="font-medium mb-4 flex items-center">
            <InformationCircleIcon className="h-4 w-4 mr-2" />
            Transaction Limits & Settings
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Minimum Exchange Value
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.minExchangeValue || 0}
                  onChange={(value) =>
                    handleInputChange("minExchangeValue", value)
                  }
                  prefix="₹"
                  step="100"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {formatCurrency(charges.minExchangeValue || 1000)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Maximum Exchange Value
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.maxExchangeValue || 0}
                  onChange={(value) =>
                    handleInputChange("maxExchangeValue", value)
                  }
                  prefix="₹"
                  step="10000"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {formatCurrency(charges.maxExchangeValue || 500000)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Verification Turnaround (hours)
              </label>
              {isEditing ? (
                <NumberInput
                  value={charges.verificationTurnaround || 24}
                  onChange={(value) =>
                    handleInputChange("verificationTurnaround", value)
                  }
                  suffix="hours"
                  step="1"
                  className="w-full"
                  defaultToZero={true}
                />
              ) : (
                <p className="font-medium">
                  {charges.verificationTurnaround || 24} hours
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={charges.requireVerification !== false}
                onChange={(e) =>
                  handleInputChange("requireVerification", e.target.checked)
                }
                disabled={!isEditing}
                className="h-4 w-4"
              />
              <label className="text-sm text-gray-700">
                Require verification for exchange items
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Exchange Policy */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Exchange Policy</h4>
        {isEditing ? (
          <textarea
            value={charges.exchangePolicy || ""}
            onChange={(e) =>
              handleInputChange("exchangePolicy", e.target.value)
            }
            className="w-full px-3 py-2 border rounded-lg"
            rows="3"
            placeholder="Enter exchange policy details..."
          />
        ) : (
          <p className="text-sm text-gray-700">
            {charges.exchangePolicy ||
              "All exchange items must be verified before final valuation. Standard deductions apply based on item condition."}
          </p>
        )}
      </div>
    </div>
  );
};

export default ExchangeChargesManagement;
